import api, { extractClientErrorMessage } from '../../../axios/axios-api'
import {
  extractTaxonomyItemsList,
  extractTurnosDisponiblesList,
  mapearSedeBase,
  mapearSedePrimerosTurnos,
  paso3CacheKey,
  paso3RangoIsoDelMes,
} from '../../../utils/helpers/asistenteTurnosCalendario'
import {
  computeAgeYearsFromIso,
  normalizeIsoDateString,
} from '../../../utils/helpers/portalDateFormat'
import { preparacionDesdeRespuestaApi } from '../../../utils/helpers/asistentePreparacionTurno'
import { interpretarRespuestaValidacionAfiliado } from '../../../utils/helpers/validarElegibilidadAfiliadoRespuesta'

function textoMontoCoseguroLimpio(raw) {
  return String(raw ?? '')
    .trim()
    .replace(/\s*-\s*$/, '')
    .trim()
}

function textoDesdeRespuestaCoseguro(data) {
  if (data == null) return ''
  if (typeof data === 'number' && Number.isFinite(data)) return String(data)
  if (typeof data === 'string') {
    const t = data.trim()
    if ((t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'))) {
      try {
        return textoDesdeRespuestaCoseguro(JSON.parse(t))
      } catch {
        return t
      }
    }
    return textoMontoCoseguroLimpio(t)
  }
  const root = data?.data ?? data
  const d = root && typeof root === 'object' ? root : data
  const cand = [d.cose, d.coseguro, d.importe, d.valor, d.mensaje, d.texto].find(
    (x) => x != null && String(x).trim() !== '',
  )
  if (cand != null) return textoMontoCoseguroLimpio(cand)
  return ''
}

function horaLabelToMinutes(horaLabel) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(horaLabel ?? '').trim())
  if (!m) return null
  return Number(m[1]) * 60 + Number(m[2])
}

function horaLikeToMinutes(hora) {
  if (hora == null || hora === '') return null
  const raw = String(hora).trim()
  const mTime = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(raw)
  if (mTime) return Number(mTime[1]) * 60 + Number(mTime[2])
  const n = Number(raw)
  if (!Number.isFinite(n)) return null
  const h = Math.floor(n / 100)
  const m = n % 100
  if (h < 0 || h > 23 || m < 0 || m > 59) return null
  return h * 60 + m
}

function positiveNumber(...values) {
  for (const value of values) {
    const n = Number(value)
    if (Number.isFinite(n) && n > 0) return n
  }
  return null
}

function practicaDisponibilidadPayload(practica) {
  const nomId = Number(practica?.nomId)
  const nomCod = String(practica?.nomCod ?? '').trim()
  const nomTurtId = Number(practica?.turtId)

  return {
    nomId: Number.isFinite(nomId) && nomId > 0 ? nomId : null,
    nomCod: nomCod || null,
    nomTurtId: Number.isFinite(nomTurtId) && nomTurtId > 0 ? nomTurtId : null,
    unidadTiempo: 1,
  }
}

function validarExtensionAdjuntoTurno(file) {
  const permitidas = ['pdf', 'png', 'jpg', 'jpeg']
  const name = String(file?.name ?? '')
  const ext = name.includes('.') ? name.split('.').pop().toLowerCase() : ''
  if (!permitidas.includes(ext)) {
    throw new Error('Formato de archivo no permitido. Solo se admiten PDF, PNG, JPG o JPEG.')
  }
}

const MENSAJE_BLOQUEO_ADVERTENCIAS_TURNOS =
  'Para solicitar turnos deberá comunicarse con la institución.'

const MENSAJE_SIN_COBERTURA_PRACTICAS_NORMALIZADO =
  'no hay practicas configuradas con cobertura para el financiador y plan indicados.'
const MENSAJE_SIN_COBERTURA_PRACTICAS_PORTAL =
  'No es posible sacar turnos con el financiador y plan seleccionados.'

function normalizarMensajePracticasAutogestion(mensaje) {
  const texto = normalizarMensajeFinanciador(mensaje)
  const normalizado = texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
  if (normalizado === MENSAJE_SIN_COBERTURA_PRACTICAS_NORMALIZADO) {
    return MENSAJE_SIN_COBERTURA_PRACTICAS_PORTAL
  }
  return texto
}

function normalizarMensajeFinanciador(mensaje) {
  return String(mensaje ?? '')
    .trim()
    .replace(/para\s+la\s+OS\/Plan\s+seleccionados/gi, 'para el financiador y plan seleccionados')
    .replace(/OS\/Plan/gi, 'financiador y plan')
    .replace(/obra social y plan/gi, 'financiador y plan')
}

export default {
  resetAsistentePasoUbicacion({ commit }) {
    commit('resetAsistentePasoUbicacion')
  },

  /**
   * Paso 2 — listado completo de ubicaciones + primer turno disponible por ubicación.
   *
   * 1) `GET Taxonomy/ubicaciones` define el universo a mostrar (todas las ubicaciones,
   *    aun las que no tengan turnos disponibles).
   * 2) `POST Turnos/primeros-turnos-por-ubicacion` enriquece esa lista con el primer
   *    turno presencial y/o videoconsulta. Si una ubicación no aparece en la respuesta
   *    queda como sede "sin turnos" (se ve en pantalla pero deshabilitada).
   *
   * El calendario completo se sigue resolviendo en el paso 3, mes a mes.
   */
  async cargarPaso2PrimerosTurnosPorUbicacion(
    { commit, state },
    { servId, prestadoresIds },
  ) {
    if (state.paso2ResumenFetchEnCurso) return

    const sid = Number(servId)
    const pre = Array.isArray(prestadoresIds)
      ? prestadoresIds.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0)
      : []

    commit('setPaso2ResumenFetchEnCurso', true)
    commit('setPaso2ResumenLoading', true)
    commit('setPaso2ResumenError', null)
    commit('setSedesConTurnoDisponible', [])
    commit('clearPaso3CalendarioCache')

    if (!Number.isFinite(sid) || sid <= 0 || pre.length === 0) {
      commit('setPaso2ResumenLoading', false)
      commit('setPaso2ResumenFetchEnCurso', false)
      return
    }

    const payloadPrimerosTurnos = {
      servId: sid,
      preId: pre,
      ubicId: -1,
      consId: -1,
      osId: Number(state.paso1Seleccion?.osId) || -1,
      soloTurnosDisponibles: true,
      incluirSesiones: false,
      filtraConsUbicxUsu: false,
      validarGruposPra: true,
      ...practicaDisponibilidadPayload(state.paso1Seleccion?.practica),
    }

    try {
      const [ubicacionesResp, primerosTurnosResp] = await Promise.all([
        api.get('Taxonomy/ubicaciones', { skipGlobalErrorAlert: true }),
        api.post('Turnos/primeros-turnos-por-ubicacion', payloadPrimerosTurnos, {
          skipGlobalErrorAlert: true,
        }),
      ])

      const sedesBase = extractTaxonomyItemsList(ubicacionesResp?.data)
        .map((u) => mapearSedeBase({ id: u?.id, nombre: u?.value }))
        .filter((s) => Number.isFinite(s.id) && s.id > 0)

      const indexConTurnos = new Map()
      const items = Array.isArray(primerosTurnosResp?.data) ? primerosTurnosResp.data : []
      for (const it of items) {
        const sede = mapearSedePrimerosTurnos(it)
        if (Number.isFinite(sede.id) && sede.id > 0) {
          indexConTurnos.set(sede.id, sede)
        }
      }

      const sedes = sedesBase.map((base) => {
        const conTurno = indexConTurnos.get(base.id)
        if (!conTurno) return base
        return { ...conTurno, nombre: conTurno.nombre || base.nombre }
      })

      sedes.sort((a, b) =>
        a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' }),
      )

      commit('setSedesConTurnoDisponible', sedes)
    } catch (e) {
      commit('setPaso2ResumenError', e)
      commit('setSedesConTurnoDisponible', [])
    } finally {
      commit('setPaso2ResumenLoading', false)
      commit('setPaso2ResumenFetchEnCurso', false)
    }
  },

  /**
   * POST `Turnos/calendario` — paso 3, calendario mensual.
   * Si es el mes actual va de hoy a fin del mes; otros meses van del 1 al fin.
   * Cachea por (ubicId, año-mes) y no refetch si ya hay datos para esa clave.
   */
  async cargarPaso3CalendarioPorMes(
    { commit, state },
    { ubicId, anio, mes, servId, prestadoresIds, force = false } = {},
  ) {
    const sid = Number(servId)
    const uid = Number(ubicId)
    const y = Number(anio)
    const m = Number(mes)
    const pre = Array.isArray(prestadoresIds)
      ? prestadoresIds.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0)
      : []
    if (
      !Number.isFinite(sid) || sid <= 0 ||
      !Number.isFinite(uid) || uid <= 0 ||
      !Number.isFinite(y) || !Number.isFinite(m) ||
      pre.length === 0
    ) {
      return
    }

    const key = paso3CacheKey(uid, y, m)
    if (!force && state.paso3CalendarioCache[key]) return

    const { fechaDesde, fechaHasta } = paso3RangoIsoDelMes(y, m)
    if (!fechaDesde || !fechaHasta) return

    commit('setPaso3CalendarioLoading', true)
    commit('setPaso3CalendarioError', null)

    const payload = {
      servId: sid,
      preId: pre,
      ubicId: uid,
      consId: -1,
      osId: Number(state.paso1Seleccion?.osId) || -1,
      fechaDesde,
      fechaHasta,
      soloTurnosDisponibles: true,
      incluirSesiones: false,
      filtraConsUbicxUsu: false,
      validarGruposPra: true,
      ...practicaDisponibilidadPayload(state.paso1Seleccion?.practica),
    }

    try {
      const { data } = await api.post('Turnos/calendario', payload, {
        skipGlobalErrorAlert: true,
      })
      const dias = extractTurnosDisponiblesList(data)
      commit('setPaso3CalendarioCacheEntry', { key, dias })
    } catch (e) {
      commit('setPaso3CalendarioError', e)
      commit('setPaso3CalendarioCacheEntry', { key, dias: [] })
    } finally {
      commit('setPaso3CalendarioLoading', false)
    }
  },

  async fetchPreparacionConfirmacion({ state }) {
    const sel = state.paso1Seleccion
    const p = sel?.practica
    if (!p?.nomCod) return { contenido: '', esHtml: false }
    const { data } = await api.get('Turnos/preparacion-practica', {
      params: {
        searchString: String(p.nomCod),
        tipoNom: Number(p.nomId) || 0,
        servId: Number(sel?.especialidadId) || 0,
      },
      skipGlobalErrorAlert: true,
    })
    return preparacionDesdeRespuestaApi(data)
  },

  async validarPracticaAsistente({ state }, { fechaIso, preIds, practica, planId } = {}) {
    const sel = state.paso1Seleccion
    const p = practica || sel?.practica
    if (!p?.nomCod) {
      return { ok: false, mensaje: 'Seleccione una práctica.', coseguro: '', raw: null }
    }
    const body = Array.isArray(preIds) && preIds.length > 0 ? preIds : []
    const qs = new URLSearchParams({
      nomId: String(p.nomId),
      nomCod: String(p.nomCod),
      planId: String(Number(planId ?? sel?.planId) || 0),
      area: 'A',
    })
    const isoNorm = normalizeIsoDateString(fechaIso)
    if (isoNorm) qs.set('fecha', isoNorm)

    try {
      const { data } = await api.post(
        `Turnos/validar-practica?${qs.toString()}`,
        body,
        { skipGlobalErrorAlert: true },
      )
      return {
        ok: true,
        mensaje: '',
        coseguro: textoDesdeRespuestaCoseguro(data),
        raw: data,
      }
    } catch (e) {
      return {
        ok: false,
        mensaje:
          normalizarMensajeFinanciador(extractClientErrorMessage(e?.response?.data)) ||
          'No se pudo validar la práctica seleccionada.',
        coseguro: '',
        raw: e?.response?.data ?? null,
      }
    }
  },

  /**
   * GET Turnos/validar-limite-por-paciente
   * 200 OK => `{ ok: true }`. 400 BadRequest => `{ ok: false, mensaje }`.
   */
  async validarLimiteTurnosPorPaciente({ rootState }) {
    const g = rootState?.guestSession
    if (g?.activo === true && g.sinFichaAutogestion === true) {
      return { ok: true, mensaje: '' }
    }
    try {
      await api.get('Turnos/validar-limite-por-paciente', {
        skipGlobalErrorAlert: true,
      })
      return { ok: true, mensaje: '' }
    } catch (e) {
      const mensaje = extractClientErrorMessage(e?.response?.data)
      return {
        ok: false,
        mensaje:
          mensaje && mensaje !== 'Solicitud no válida.'
            ? mensaje
            : MENSAJE_BLOQUEO_ADVERTENCIAS_TURNOS,
      }
    }
  },

  /**
   * GET Turnos/validar-cant-turnos-web
   * 200 OK => `{ ok: true }`. 400 BadRequest => `{ ok: false, mensaje }`.
   */
  async validarCantTurnosWeb({ rootState }, { fichaId } = {}) {
    const payloadFichaId = Number(fichaId)
    const activeFichaId = Number(rootState?.portalHome?.activePortalFichaId)
    const guestFichaId = Number(rootState?.guestSession?.fichaId)
    const effectiveFichaId =
      Number.isFinite(payloadFichaId) && payloadFichaId > 0
        ? payloadFichaId
        : Number.isFinite(activeFichaId) && activeFichaId > 0
        ? activeFichaId
        : Number.isFinite(guestFichaId) && guestFichaId > 0
        ? guestFichaId
        : null
    const params =
      Number.isFinite(effectiveFichaId) && effectiveFichaId > 0
        ? { fichaId: effectiveFichaId }
        : undefined
    try {
      await api.get('Turnos/validar-cant-turnos-web', {
        params,
        skipGlobalErrorAlert: true,
      })
      return { ok: true, mensaje: '' }
    } catch (e) {
      return { ok: false, mensaje: extractClientErrorMessage(e?.response?.data) }
    }
  },

  /**
   * GET Turnos/validar-limite-por-servicio?servId=
   * 200 OK => `{ ok: true }`. 400 BadRequest => `{ ok: false, mensaje }`.
   */
  async validarLimiteTurnosPorServicio(_ctx, { servId } = {}) {
    const sid = Number(servId)
    if (!Number.isFinite(sid) || sid <= 0) {
      return { ok: false, mensaje: 'Especialidad no válida.' }
    }
    try {
      await api.get('Turnos/validar-limite-por-servicio', {
        params: { servId: sid },
        skipGlobalErrorAlert: true,
      })
      return { ok: true, mensaje: '' }
    } catch (e) {
      return { ok: false, mensaje: extractClientErrorMessage(e?.response?.data) }
    }
  },

  /**
   * GET Turnos/validar-limite-por-prestador?preId=
   * 200 OK => `{ ok: true }`. 400 BadRequest => `{ ok: false, mensaje }`.
   */
  async validarLimiteTurnosPorPrestador({ rootState }, { preId, fichaId } = {}) {
    const pid = Number(preId)
    if (!Number.isFinite(pid) || pid <= 0) {
      return { ok: false, mensaje: 'Profesional no válido.' }
    }
    const payloadFichaId = Number(fichaId)
    const activeFichaId = Number(rootState?.portalHome?.activePortalFichaId)
    const guestFichaId = Number(rootState?.guestSession?.fichaId)
    const effectiveFichaId =
      Number.isFinite(payloadFichaId) && payloadFichaId > 0
        ? payloadFichaId
        : Number.isFinite(activeFichaId) && activeFichaId > 0
        ? activeFichaId
        : Number.isFinite(guestFichaId) && guestFichaId > 0
        ? guestFichaId
        : null
    const params = { preId: pid }
    if (Number.isFinite(effectiveFichaId) && effectiveFichaId > 0) {
      params.fichaId = effectiveFichaId
    }
    try {
      await api.get('Turnos/validar-limite-por-prestador', {
        params,
        skipGlobalErrorAlert: true,
      })
      return { ok: true, mensaje: '' }
    } catch (e) {
      return { ok: false, mensaje: extractClientErrorMessage(e?.response?.data) }
    }
  },

  async verificarDisponibilidadTurno({ state }) {
    const sel = state.paso1Seleccion
    const slot = state.paso3SlotSeleccionado
    const servId = Number(sel?.especialidadId)
    const preId = Number(slot?.preId)
    const fechaIso = String(slot?.fechaIso ?? '').trim()
    const m = /^(\d{1,2}):(\d{2})/.exec(String(slot?.horaLabel ?? '').trim())
    const hsIniMin = m ? Number(m[1]) * 60 + Number(m[2]) : NaN
    const hsIni = m ? Number(m[1]) * 100 + Number(m[2]) : NaN
    if (
      !Number.isFinite(servId) || servId <= 0 ||
      !Number.isFinite(preId) || preId <= 0 ||
      !fechaIso || !Number.isFinite(hsIni)
    ) {
      return {
        ok: false,
        mensaje: 'No se pudo verificar la disponibilidad del turno seleccionado.',
      }
    }
    const consIdRaw = Number(slot?.consId)
    const consId = Number.isFinite(consIdRaw) && consIdRaw > 0 ? consIdRaw : -1
    const hsFinSlotMin = horaLikeToMinutes(slot?.hsFin)
    const duracion = positiveNumber(
      sel?.practica?.turtTiempo,
      slot?.turtTiempo,
      slot?.turnoTiempo,
      hsFinSlotMin != null && hsFinSlotMin > hsIniMin ? hsFinSlotMin - hsIniMin : null,
    )
    const hsFinMin = duracion ? hsIniMin + duracion : null
    const hsFin = hsFinMin != null
      ? (Math.floor(hsFinMin / 60) * 100) + (hsFinMin % 60)
      : null
    const payload = {
      servId,
      fecha: `${fechaIso}T00:00:00`,
      hsIni,
      hsFin,
      preId,
      consId,
      turnoId: -1,
    }
    try {
      const { data } = await api.post('Turnos/verificar-disponibilidad', payload, {
        skipGlobalErrorAlert: true,
      })
      const disponible =
        data === true ||
        data === 'true' ||
        data?.disponible === true ||
        data?.data === true
      return {
        ok: disponible,
        ocupado:
          data === false || data === 'false' ||
          data?.disponible === false || data?.data === false,
        mensaje: disponible
          ? ''
          : 'El turno seleccionado ya no está disponible. Por favor elija otro.',
      }
    } catch (e) {
      return {
        ok: false,
        mensaje:
          extractClientErrorMessage(e?.response?.data) ||
          'No se pudo verificar la disponibilidad del turno. Intente nuevamente.',
      }
    }
  },

  /**
   * POST Turnos/obras-sociales/{osId}/validar-elegibilidad-afiliado
   * @returns {Promise<{ ok: boolean, mensaje: string, raw: unknown }>}
   */
  async validarElegibilidadAfiliadoAsistente(_ctx, { osId, body }) {
    const id = Number(osId)
    if (!Number.isFinite(id) || id <= 0) {
      return { ok: false, mensaje: 'Financiador no válido.', raw: null }
    }
    const { data } = await api.post(
      `Turnos/obras-sociales/${id}/validar-elegibilidad-afiliado`,
      body,
      { skipGlobalErrorAlert: true },
    )
    const { ok, mensaje } = interpretarRespuestaValidacionAfiliado(data)
    return { ok, mensaje, raw: data }
  },

  async interpretarPedidoMedico(_ctx, { file } = {}) {
    if (!(file instanceof File)) {
      throw new Error('Debe adjuntar un pedido médico.')
    }
    const form = new FormData()
    form.append('file', file, file.name)
    const { data } = await api.post('Turnos/pedido-medico/interpretar', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
      skipGlobalErrorAlert: true,
    })
    return data
  },

  async fetchPedidosMedicosVinculables(_ctx, { fichaId, pedEstEId } = {}) {
    const params = {}
    const fid = Number(fichaId)
    if (Number.isFinite(fid) && fid > 0) params.fichaId = fid
    const pedidoId = Number(pedEstEId)
    if (Number.isFinite(pedidoId) && pedidoId > 0) params.pedEstEId = pedidoId
    const { data } = await api.post('Turnos/pedidos-medicos-vinculables', params, {
      skipGlobalErrorAlert: true,
    })
    return Array.isArray(data) ? data : []
  },

  async adjuntarArchivoTurno(_ctx, { file, turnoId, fichaId } = {}) {
    if (!(file instanceof File)) {
      throw new Error('Debe adjuntar un archivo.')
    }
    validarExtensionAdjuntoTurno(file)
    const idTurno = Number(turnoId)
    const idFicha = Number(fichaId)
    if (!Number.isFinite(idTurno) || idTurno <= 0) {
      throw new Error('Turno inválido.')
    }
    if (!Number.isFinite(idFicha) || idFicha <= 0) {
      throw new Error('Ficha inválida.')
    }
    const form = new FormData()
    form.append('file', file, file.name)
    form.append('turnoId', String(idTurno))
    form.append('fichaId', String(idFicha))
    const { data } = await api.post('Turnos/adjuntar-archivo-turno', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
      skipGlobalErrorAlert: true,
    })
    return data
  },

  async fetchAsistenteEspecialidades() {
    const { data } = await api.get('Turnos/servicios')
    return Array.isArray(data?.data) ? data.data : []
  },

  async fetchAsistenteProfesionales(_ctx, { servId } = {}) {
    const sid = Number(servId)
    if (!Number.isFinite(sid) || sid <= 0) return []
    const { data } = await api.get('Turnos/prestadores', {
      params: { servId: sid },
    })
    return Array.isArray(data?.data) ? data.data : []
  },

  async fetchFichaFinanciadores(_ctx, { fichaId } = {}) {
    const params = {}
    const fid = Number(fichaId)
    if (Number.isFinite(fid) && fid > 0) params.fichaId = fid
    const { data } = await api.get('Ficha/financiadores', { params })
    if (!Array.isArray(data)) throw new Error('Respuesta de financiadores inválida.')
    return data
  },
  async fetchFichaPlanes(_ctx, { osId, fichaId } = {}) {
    const id = Number(osId)
    if (!Number.isFinite(id) || id <= 0) return []
    const params = { osId: id }
    const fid = Number(fichaId)
    if (Number.isFinite(fid) && fid > 0) params.fichaId = fid
    const { data } = await api.get('Ficha/planes', { params })
    return Array.isArray(data) ? data : []
  },

  async fetchAdjuntoObligatorioTurno(_ctx, { osId, planId } = {}) {
    const oid = Number(osId)
    if (!Number.isFinite(oid) || oid <= 0) {
      return {
        permiteAdjuntarArchivo: false,
        requiereArchivoAdjunto: false,
        reglaGeneralFinanciador: false,
        descripcionVisiblePaciente: '',
      }
    }
    const params = { osId: oid }
    const pid = Number(planId)
    if (Number.isFinite(pid) && pid > 0) params.planId = pid
    const { data } = await api.get('Turnos/adjunto-obligatorio', {
      params,
      skipGlobalErrorAlert: true,
    })
    const root = data?.data ?? data ?? {}
    return {
      permiteAdjuntarArchivo:
        root.permiteAdjuntarArchivo === true || root.PermiteAdjuntarArchivo === true,
      requiereArchivoAdjunto:
        root.requiereArchivoAdjunto === true || root.RequiereArchivoAdjunto === true,
      reglaGeneralFinanciador:
        root.reglaGeneralFinanciador === true || root.ReglaGeneralFinanciador === true,
      descripcionVisiblePaciente: String(
        root.descripcionVisiblePaciente ?? root.DescripcionVisiblePaciente ?? '',
      ).trim(),
    }
  },

  async fetchAsistentePracticas(_ctx, { servId, planId } = {}) {
    const params = {}
    const sid = Number(servId)
    const pid = Number(planId)
    if (Number.isFinite(sid) && sid > 0) params.servId = sid
    if (Number.isFinite(pid) && pid > 0) params.planId = pid
    try {
      const { data } = await api.get('Turnos/practicas-autogestion', {
        params,
        skipGlobalErrorAlert: true,
      })
      const items = Array.isArray(data?.items) ? data.items : []
      return {
        items: items.map((it, index) => {
          const alias = String(it?.alias ?? '').trim()
          const nomNom = String(it?.nomNom ?? '').trim()
          const nomId = Number(it?.nomId) || 0
          const nomCod = String(it?.nomCod ?? '')
          const nombre = (alias || nomNom).toUpperCase()
          const turtId = Number(it?.turtId ?? it?.TurtId)
          const codigosAsociados = Array.isArray(it?.codigosAsociados)
            ? it.codigosAsociados
                .map((codigo) => ({
                  nomId: Number(codigo?.nomId ?? codigo?.NomId) || 0,
                  nomCod: String(codigo?.nomCod ?? codigo?.NomCod ?? '').trim(),
                  nomNom: String(codigo?.nomNom ?? codigo?.NomNom ?? '').trim(),
                }))
                .filter((codigo) => codigo.nomId > 0 && codigo.nomCod)
            : []
          return {
            key: `${nomId}|${nomCod}|${nombre}|${index}`,
            nomId,
            nomCod,
            nombre,
            nomNom,
            nombrePracticaGuardar: nomNom,
            codigosAsociados,
            turtId: Number.isFinite(turtId) ? turtId : 0,
          }
        }),
        mensaje: '',
      }
    } catch (e) {
      return {
        items: [],
        mensaje: normalizarMensajePracticasAutogestion(
          extractClientErrorMessage(e?.response?.data),
        ),
      }
    }
  },

  async guardarTurnoAsistente({ state }, { slot, ficha, contacto }) {
    const sel = state.paso1Seleccion
    const p = sel?.practica
    if (!p?.nomCod) return { ok: false, turnoId: 0, mensaje: '', raw: null }

    const startMin = horaLabelToMinutes(slot?.horaLabel) ?? 0
    const hsFinSlotMin = horaLikeToMinutes(slot?.hsFin)
    const duracionDesdeHsFin =
      hsFinSlotMin != null && hsFinSlotMin > startMin ? hsFinSlotMin - startMin : null
    const turtTiempo = positiveNumber(
      p.turtTiempo,
      slot?.turtTiempo,
      slot?.turnoTiempo,
      duracionDesdeHsFin,
    )
    if (!turtTiempo) {
      return {
        ok: false,
        turnoId: 0,
        mensaje: 'No se pudo determinar la duración del turno seleccionado.',
        raw: null,
      }
    }
    const endMin = startMin + turtTiempo
    const pad = (n) => String(n).padStart(2, '0')
    const horaIni = `${pad(Math.floor(startMin / 60))}${pad(startMin % 60)}`
    const horaFin = `${pad(Math.floor(endMin / 60))}${pad(endMin % 60)}`

    const fichaNum = Number(ficha?.id)
    const tieneFicha = Number.isFinite(fichaNum) && fichaNum > 0
    const pedEstPId = Number(sel?.pedEstPId)
    const pedidoMedicoEsperado = !!sel?.pedidoMedicoSeleccionado
    const tienePedidoMedico =
      pedidoMedicoEsperado && Number.isFinite(pedEstPId) && pedEstPId > 0
    if (pedidoMedicoEsperado && !tienePedidoMedico) {
      return {
        ok: false,
        turnoId: 0,
        mensaje: 'No se pudo asociar el pedido medico al turno. Vuelva a seleccionar el pedido medico.',
        raw: null,
      }
    }
    const practicasAsociadasPedido = tienePedidoMedico && Array.isArray(sel?.practicasAsociadas)
      ? sel.practicasAsociadas.filter((practica) => {
        const practicaPedEstPId = Number(practica?.pedEstPId ?? practica?.PedEstPId)
        return Number.isFinite(practicaPedEstPId) && practicaPedEstPId > 0
      })
      : []

    const payload = {
      turnoId: 0,
      esSobreturno: false,
      servid: Number(sel?.especialidadId) || 0,
      fecha: normalizeIsoDateString(slot?.fechaIso),
      horaIni,
      horaFin,
      turtTiempo,
      fichaId: tieneFicha ? fichaNum : 0,
      tipoDoc: Number(ficha?.tdId),
      nroDoc: String(ficha?.nroDoc ?? '').trim(),
      nroHc: String(ficha?.nroHc ?? '').trim(),
      apellido: String(ficha?.apellido ?? '').trim(),
      nombre: String(ficha?.nombre ?? '').trim(),
      sexo: String(ficha?.sexo ?? 'M').trim().slice(0, 1).toUpperCase() || 'M',
      edad: computeAgeYearsFromIso(ficha?.fechaNacimiento) ?? 0,
      telefono: String(contacto?.telefono ?? '').trim(),
      osId: Number(sel?.osId) || 0,
      planId: Number(sel?.planId) || 0,
      preId: Number(slot?.preId),
      nomId: Number(p.nomId),
      codigoPractica: String(p.nomCod),
      nombrePractica: String(p.nombrePracticaGuardar || p.nomNom || p.nombre || ''),
      pedEstPId: tienePedidoMedico ? pedEstPId : null,
      turtId: Number(p.turtId) || Number(slot?.turtId) || 0,
      observaciones: String(contacto?.observaciones ?? '').trim(),
      consId: Number(slot?.consId),
      nroAfiliado: String(sel?.nroAfiliado ?? '').trim(),
      celular: String(contacto?.celular ?? '').trim(),
      email: String(contacto?.email ?? '').trim(),
      fechaNacimiento: normalizeIsoDateString(ficha?.fechaNacimiento) || '',
      esVideoConsulta: !!slot?.esVideo,
      turnoBorradoId: null,
      generaFichaDesdeTurno: !tieneFicha,
      edadUnidad: 'A',
      croId: Number(slot?.croId),
      practicasAsociadas: practicasAsociadasPedido,
      practicasAsociadasGeneral: [],
      tieneArchivoAdjunto: Array.isArray(sel?.pedidoMedicoArchivos) &&
        sel.pedidoMedicoArchivos.length > 0,
    }

    try {
      const { data } = await api.post('Turnos/guardar-turno', payload, {
        skipGlobalErrorAlert: true,
      })
      const turnoId = Number(data?.turnoId ?? data?.TurnoId ?? data) || 0
      const confirmadoAutomaticamente =
        data?.confirmadoAutomaticamente ?? data?.ConfirmadoAutomaticamente
      const noConfirmarAutomaticamente =
        data?.noConfirmarAutomaticamente ?? data?.NoConfirmarAutomaticamente
      const requiereConfirmacionPorMail =
        data?.requiereConfirmacionPorMail ?? data?.RequiereConfirmacionPorMail
      const validaTurnoWeb =
        data?.validaTurnoWeb ?? data?.ValidaTurnoWeb
      return {
        ok: turnoId > 0,
        turnoId,
        confirmadoAutomaticamente:
          typeof confirmadoAutomaticamente === 'boolean'
            ? confirmadoAutomaticamente
            : undefined,
        noConfirmarAutomaticamente:
          typeof noConfirmarAutomaticamente === 'boolean'
            ? noConfirmarAutomaticamente
            : undefined,
        requiereConfirmacionPorMail:
          typeof requiereConfirmacionPorMail === 'boolean'
            ? requiereConfirmacionPorMail
            : undefined,
        validaTurnoWeb:
          typeof validaTurnoWeb === 'boolean' ? validaTurnoWeb : undefined,
        mensaje: '',
        raw: data,
      }
    } catch (e) {
      return {
        ok: false,
        turnoId: 0,
        mensaje: extractClientErrorMessage(e?.response?.data),
        ocupado: e?.response?.data?.error === 'turno_no_disponible',
        raw: e,
      }
    }
  },
}
